import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import { Text, Card, Chip, Button, TextInput, Divider, ActivityIndicator } from 'react-native-paper';
import { Picker } from '@react-native-picker/picker';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';

const TenantDetalleScreen = ({ navigation, route }) => {
  const { tenantId } = route.params;
  const [tenant, setTenant] = useState(null);
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const loadTenant = async () => {
    try {
      const data = await api.superAdmin.getTenant(tenantId);
      setTenant(data);
      setEditForm({
        nombre: data.nombre || '',
        email_contacto: data.email_contacto || '',
        telefono: data.telefono || '',
        plan_id: data.plan_id || null,
      });
    } catch (error) {
      Alert.alert('Error', 'No se pudo cargar el tenant');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const loadPlanes = async () => {
    try {
      const data = await api.superAdmin.getPlanes();
      setPlanes(Array.isArray(data) ? data : data.planes || []);
    } catch (error) {
      // No bloqueante: si falla, simplemente no se muestra el selector de plan
      console.warn('No se pudieron cargar los planes:', error?.message);
    }
  };

  useEffect(() => {
    loadTenant();
    loadPlanes();
  }, []);

  const handleAction = async (action, label) => {
    Alert.alert(
      'Confirmar',
      `Estas seguro de ${label} esta empresa?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Confirmar',
          onPress: async () => {
            setActionLoading(true);
            try {
              await action(tenantId);
              Alert.alert('Exito', `Empresa ${label} exitosamente`);
              loadTenant();
            } catch (error) {
              Alert.alert('Error', error.message);
            } finally {
              setActionLoading(false);
            }
          },
        },
      ]
    );
  };

  const handleSaveEdit = async () => {
    setActionLoading(true);
    try {
      await api.superAdmin.updateTenant(tenantId, editForm);
      Alert.alert('Exito', 'Informacion actualizada');
      setEditing(false);
      loadTenant();
    } catch (error) {
      Alert.alert('Error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const getEstadoColor = (estado) => {
    const colors = { activo: '#4CAF50', pendiente: '#FF9800', suspendido: '#f44336', cancelado: '#9E9E9E' };
    return colors[estado] || '#9E9E9E';
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
      </View>
    );
  }

  if (!tenant) return null;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Header con estado */}
      <View style={styles.headerCard}>
        <Text style={styles.tenantName}>{tenant.nombre}</Text>
        <Chip
          style={{ backgroundColor: getEstadoColor(tenant.estado) + '20', alignSelf: 'flex-start', marginTop: 8 }}
          textStyle={{ color: getEstadoColor(tenant.estado) }}
        >
          {tenant.estado?.toUpperCase()}
        </Chip>
        <Text style={styles.tenantSlug}>Codigo: {tenant.slug}</Text>
      </View>

      {/* Info editable */}
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.cardHeader}>
            <Text style={styles.cardTitle}>Informacion General</Text>
            <Button
              mode="text"
              onPress={() => setEditing(!editing)}
              textColor="#007bff"
              compact
            >
              {editing ? 'Cancelar' : 'Editar'}
            </Button>
          </View>

          {editing ? (
            <>
              <TextInput
                label="Nombre"
                value={editForm.nombre}
                onChangeText={(v) => setEditForm(p => ({ ...p, nombre: v }))}
                style={styles.input}
              />
              <TextInput
                label="Email de contacto"
                value={editForm.email_contacto}
                onChangeText={(v) => setEditForm(p => ({ ...p, email_contacto: v }))}
                style={styles.input}
                keyboardType="email-address"
              />
              <TextInput
                label="Telefono"
                value={editForm.telefono}
                onChangeText={(v) => setEditForm(p => ({ ...p, telefono: v }))}
                style={styles.input}
                keyboardType="phone-pad"
              />
              {planes.length > 0 && (
                <View style={styles.planSelector}>
                  <Text style={styles.planSelectorLabel}>Plan asignado</Text>
                  <View style={styles.pickerWrapper}>
                    <Picker
                      selectedValue={editForm.plan_id}
                      onValueChange={(v) => setEditForm(p => ({ ...p, plan_id: v }))}
                    >
                      {planes.map(plan => (
                        <Picker.Item
                          key={plan.id}
                          label={`${plan.nombre}${plan.precio_mensual > 0 ? ` — L. ${parseFloat(plan.precio_mensual).toFixed(2)}/mes` : ' (gratis)'}`}
                          value={plan.id}
                        />
                      ))}
                    </Picker>
                  </View>
                </View>
              )}
              <Button
                mode="contained"
                onPress={handleSaveEdit}
                loading={actionLoading}
                style={{ marginTop: 12, backgroundColor: '#007bff' }}
              >
                Guardar Cambios
              </Button>
            </>
          ) : (
            <>
              <InfoRow label="Email" value={tenant.email_contacto} icon="mail-outline" />
              <InfoRow label="Telefono" value={tenant.telefono} icon="call-outline" />
              <InfoRow label="RTN" value={tenant.rtn} icon="card-outline" />
              <InfoRow label="Plan" value={tenant.plan_nombre} icon="pricetag-outline" />
              <InfoRow
                label="Creado"
                value={tenant.created_at ? new Date(tenant.created_at).toLocaleDateString('es-HN') : '-'}
                icon="calendar-outline"
              />
            </>
          )}
        </Card.Content>
      </Card>

      {/* Estadisticas */}
      {(tenant.stats || tenant.total_usuarios !== undefined) && (
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.cardTitle}>Estadisticas de Uso</Text>
            <View style={styles.statsGrid}>
              <StatBox label="Usuarios" value={tenant.stats?.usuarios || tenant.total_usuarios || 0} color="#1565C0" />
              <StatBox label="Productos" value={tenant.stats?.productos || tenant.total_productos || 0} color="#2E7D32" />
              <StatBox label="Clientes" value={tenant.stats?.clientes || 0} color="#6A1B9A" />
              <StatBox label="Ventas" value={tenant.stats?.ventas || tenant.total_ventas || 0} color="#E65100" />
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Acciones */}
      <Card style={styles.card}>
        <Card.Content>
          <Text style={styles.cardTitle}>Acciones</Text>

          {tenant.estado === 'pendiente' && (
            <Button
              mode="contained"
              onPress={() => handleAction(api.superAdmin.activarTenant, 'activar')}
              loading={actionLoading}
              style={[styles.actionButton, { backgroundColor: '#4CAF50' }]}
              icon="checkmark-circle-outline"
            >
              Activar Empresa
            </Button>
          )}

          {tenant.estado === 'activo' && (
            <Button
              mode="contained"
              onPress={() => handleAction(api.superAdmin.suspenderTenant, 'suspender')}
              loading={actionLoading}
              style={[styles.actionButton, { backgroundColor: '#FF9800' }]}
              icon="pause-circle-outline"
            >
              Suspender Empresa
            </Button>
          )}

          {tenant.estado === 'suspendido' && (
            <>
              <Button
                mode="contained"
                onPress={() => handleAction(api.superAdmin.activarTenant, 'reactivar')}
                loading={actionLoading}
                style={[styles.actionButton, { backgroundColor: '#4CAF50' }]}
                icon="checkmark-circle-outline"
              >
                Reactivar Empresa
              </Button>
            </>
          )}

          {tenant.estado !== 'cancelado' && (
            <Button
              mode="outlined"
              onPress={() => handleAction(api.superAdmin.cancelarTenant, 'cancelar')}
              loading={actionLoading}
              style={[styles.actionButton, { borderColor: '#f44336' }]}
              textColor="#f44336"
              icon="close-circle-outline"
            >
              Cancelar Empresa
            </Button>
          )}
        </Card.Content>
      </Card>
    </ScrollView>
  );
};

const InfoRow = ({ label, value, icon }) => (
  <View style={styles.infoRow}>
    <Ionicons name={icon} size={18} color="#888" />
    <Text style={styles.infoLabel}>{label}:</Text>
    <Text style={styles.infoValue}>{value || '-'}</Text>
  </View>
);

const StatBox = ({ label, value, color }) => (
  <View style={[styles.statBox, { borderLeftColor: color }]}>
    <Text style={[styles.statValue, { color }]}>{value}</Text>
    <Text style={styles.statLabel}>{label}</Text>
  </View>
);

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  content: { padding: 16, paddingBottom: 40 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  headerCard: { backgroundColor: 'white', borderRadius: 12, padding: 20, marginBottom: 12, elevation: 1 },
  tenantName: { fontSize: 22, fontWeight: 'bold', color: '#333' },
  tenantSlug: { fontSize: 14, color: '#007bff', marginTop: 8 },
  card: { marginBottom: 12, backgroundColor: 'white' },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  cardTitle: { fontSize: 16, fontWeight: 'bold', color: '#333', marginBottom: 12 },
  input: { marginBottom: 10, backgroundColor: '#f9f9f9' },
  infoRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f5f5f5' },
  infoLabel: { fontSize: 14, color: '#666', marginLeft: 8, width: 80 },
  infoValue: { fontSize: 14, color: '#333', fontWeight: '500', flex: 1 },
  statsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  statBox: { flex: 1, minWidth: '45%', backgroundColor: '#f9f9f9', borderRadius: 8, padding: 12, borderLeftWidth: 3 },
  statValue: { fontSize: 24, fontWeight: 'bold' },
  statLabel: { fontSize: 12, color: '#888', marginTop: 4 },
  actionButton: { marginBottom: 10 },
  planSelector: { marginTop: 4, marginBottom: 6 },
  planSelectorLabel: { fontSize: 12, color: '#666', marginBottom: 6, marginLeft: 4 },
  pickerWrapper: { borderWidth: 1, borderColor: '#ddd', borderRadius: 8, backgroundColor: '#fafafa', overflow: 'hidden' },
});

export default TenantDetalleScreen;
